import { toQueryParams } from '@shared/utils/url';
import { merge } from 'lodash-es';
import { createPaginatedGetter } from './actions/pagination';
import type { ClientConfig } from './ClientConfig';
import { defaultOptions } from './defaults';
import { createHeaders } from './headers';
import { handleResponse } from './response';
import { validateData } from './schema';
import type { Options } from './types';

export type { ClientConfig };

export interface ApiClientScope {
  get<T>(path: string, options?: Options): Promise<T>;
  put<T>(path: string, body?: unknown, options?: Options): Promise<T>;
  post<T>(path: string, body: unknown, options?: Options): Promise<T>;
  patch<T>(path: string, body: unknown, options?: Options): Promise<T>;
  postFile<T>(path: string, file: File, options?: Options): Promise<T>;
  remove<T>(path: string, body?: unknown, options?: Options): Promise<T>;
  getPaginated<T>(
    path: string,
    params?: unknown,
    options?: Options,
    pageProp?: string
  ): Promise<T[]>;
}

export interface ApiClient extends ApiClientScope {
  scope(baseUrl: string): ApiClientScope;
  getHeaders(override?: HeadersInit): Headers;
}

export function createClient(config: ClientConfig = {}): ApiClient {
  function getHeaders(override?: HeadersInit): Headers {
    return createHeaders(config.headers?.(), override);
  }

  function createScopedClient(baseUrl: string): ApiClientScope {
    function request<T>(
      path: string,
      requestInit: RequestInit,
      opts?: Options
    ): Promise<T> {
      const options = merge({}, defaultOptions, { baseUrl }, opts);
      const controller = new AbortController();
      const finalRequestInit = {
        ...requestInit,
        ...options.requestInit,
        headers: getHeaders(
          options.requestInit?.headers ?? requestInit.headers
        ),
        signal: controller.signal,
      };
      const url = `${options.baseUrl}/${path}${toQueryParams(
        options.queryParams
      )}`;
      const request = new Request(url, finalRequestInit);

      const promise = fetch(request)
        .then((response) => handleResponse(response, request, options, config))
        .then(validateData(path, options, config));

      // @ts-expect-error
      promise.cancel = () => controller.abort();

      return promise;
    }

    function get<T>(path: string, options?: Options): Promise<T> {
      return request<T>(path, { method: 'GET' }, options);
    }

    function put<T>(path: string, body?: any, options?: Options): Promise<T> {
      return request<T>(
        path,
        { method: 'PUT', body: JSON.stringify(body) },
        options
      );
    }

    function post<T>(path: string, body: any, options?: Options): Promise<T> {
      return request<T>(
        path,
        { method: 'POST', body: JSON.stringify(body) },
        options
      );
    }

    function patch<T>(path: string, body: any, options?: Options): Promise<T> {
      return request<T>(
        path,
        { method: 'PATCH', body: JSON.stringify(body) },
        options
      );
    }

    function postFile<T>(
      path: string,
      file: File,
      options?: Options
    ): Promise<T> {
      return request<T>(
        path,
        { method: 'POST', body: file, headers: { 'Content-Type': file.type } },
        options
      );
    }

    function remove<T>(
      path: string,
      body?: any,
      options?: Options
    ): Promise<T> {
      return request<T>(
        path,
        { method: 'DELETE', body: JSON.stringify(body) },
        { responseType: 'void', ...options }
      );
    }

    const getPaginated = createPaginatedGetter(get);

    return { get, put, post, patch, postFile, remove, getPaginated };
  }

  return {
    ...createScopedClient(''),
    scope: createScopedClient,
    getHeaders,
  };
}
